import { toast } from 'react-toastify';

import { browserHistory } from 'src/history';
import { store, persistor } from 'src/store';
import { PATHS, TYPES } from 'src/common/constants';

type SessionEndReason = 'expired' | 'user-initiated';

interface HandleSessionExpiredOptions {
  reason?: SessionEndReason;
}

const TOAST_GUARD_WINDOW_MS = 5_000;
let lastFiredAt = 0;

export const handleSessionExpired = async (
  options: HandleSessionExpiredOptions = {}
): Promise<void> => {
  const { reason = 'expired' } = options;

  /* Idempotency: a single 401 from the server can fan out into many failing
   * dispatches/requests in a short window. Without this guard the user sees a
   * cascade of toasts and redirect attempts. */
  const now: number = Date.now();

  if (now - lastFiredAt < TOAST_GUARD_WINDOW_MS) {
    return;
  }

  lastFiredAt = now;

  if (reason === 'expired') {
    toast.error(' 🚫 Your session has timed out, please login again.', {
      autoClose: 12000,
      position: 'top-center',
    });
  }

  /* FIXME(auth-consolidation): once PR 2 lands, the manual localStorage chain
   * below collapses to just `dispatch(AUTH_SIGN_OUT) + persistor.purge()`.
   *
   * The chain exists because this codebase currently stores auth state in TWO
   * places: inside the Redux store (which redux-persist mirrors into the
   * `persist:root` key), AND as standalone localStorage keys written
   * imperatively by scattered legacy code (non-React thunks, intervals,
   * upload helpers). `persistor.purge()` clears the redux-persist snapshot;
   * the manual removeItem calls clear the parallel standalone keys that
   * redux-persist doesn't know about.
   *
   * See ASANA-XXXX for the consolidation work that removes this duplication.
   */
  localStorage.removeItem('userId');
  localStorage.setItem('isSignedOut', 'true');
  localStorage.removeItem('auth');
  localStorage.removeItem('user');
  localStorage.removeItem('persist:root');
  localStorage.removeItem('IsHomerNavigatFromFeedBackTemplate');
  localStorage.removeItem('isAnnotationImageUpload');

  store.dispatch({ type: TYPES.AUTH_SIGN_OUT });

  await persistor.purge();

  browserHistory.push(PATHS.AUTH_SIGN_IN);
};